// Sabit Rapor · 5020 — Müşteri Listesi (Raporlar → Tanımlamalar → Müşteri).
//
// İki ekran:
//   1) CustomerReportCriteriaPage — kriter formu. "Raporu Göster" ile filtreleri query
//      string'e serileştirip yeni sekmede (?embed=true → NavMenu'suz tam ekran) liste açar.
//   2) CustomerReportListPage — filtrelere göre /api/customers OData listesi (sayfalama +
//      Excel/Yazdır). Yeni sekmede açıldığı için localStorage token'ı ile kimlik doğrular.
//
// TÜM kriter/kolonlar Customer entity'sinde GERÇEKTEN var olan alanlara dayanır
// (DB'ye yeni alan eklenmez): VendorId, GroupId, SubGroupId, SalesRepresentativeId/RepId,
// Status, TaxType, Region, City, TextId, TaxNumber, CreditLimit1/2, DueDate, CreatedDate,
// Phone/CellPhone, TaxOffice, District.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams} from 'react-router-dom';
import { authHeaders,
  buildODataUrl,
  getODataRows,
  exportToExcel } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { ReportGrid } from './reports/ReportGrid';
import { MultiSelect } from './reports/MultiSelect';
import { DateInput } from './reports/DateInput';

const API_BASE = '/api/customers';
const REPORT_CODE = '5020';
const REPORT_TITLE = 'Müşteri Listesi';

// ── Sabit seçenek listeleri (Customer entity kodlarına birebir) ──
const STATUS_OPTIONS = [
  { value: '0', label: 'Aktif' },
  { value: '1', label: 'Pasif' },
  { value: '2', label: 'Beklemede' },
];
const STATUS_LABELS = { 0: 'Aktif', 1: 'Pasif', 2: 'Beklemede' };
const YESNO_OPTIONS = [
  { value: 'true', label: 'Evet' },
  { value: 'false', label: 'Hayır' },
];
// ST (satış temsilcisi) ataması — değerler CustomersController.Get'in repAssignment
// parametresiyle birebir aynı olmalı. Boş = kriter uygulanmaz.
const REP_ASSIGNMENT_OPTIONS = [
  { value: 'assigned', label: "ST'ye atanmış" },
  { value: 'multi', label: "Birden fazla ST'ye atanmış" },
  { value: 'none', label: "ST'ye atanmamış" },
];
const REP_ASSIGNMENT_LABELS = Object.fromEntries(REP_ASSIGNMENT_OPTIONS.map((o) => [o.value, o.label]));
const CREDIT_OPS = [
  { value: 'eq', label: '=' },
  { value: 'gt', label: '>' },
  { value: 'ge', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'le', label: '≤' },
];

// ── Yardımcılar ──
function normalizeList(payload) { if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return []; }
const pick = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k];
  return undefined; };
const optId = (o) => pick(o, 'id', 'Id');
const optCode = (o) => pick(o, 'textId', 'TextId', 'erpId', 'ErpId');
// VKN/TCKN alanları: yalnız rakam, en fazla `max` hane. maxLength tek başına yetmiyor —
// yapıştırma ve harf girişini de eleyelim (tester: harf girilebiliyordu, sınır yoktu).
const digitsOnly = (v, max) => String(v ?? '').replace(/\D/g, '').slice(0, max);
const optName = (o) => pick(o, 'description', 'Description', 'name', 'Name', 'title', 'Title');
const optLabel = (o) => { const code = optCode(o);
  const name = optName(o);
  if (code && name) return `${code} - ${name}`;
  return name || code || '—'; };
const nf2 = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney = (v) => nf2.format(Number(v) || 0);
const esc = (v) => String(v).replace(/'/g, "''");

// Kriter alanlarının anlamlı olduğu tüm keyler (query string ⇄ state köprüsü).
// Tümü Customer tablosunda GERÇEKTEN dolu olan kolonlara karşılık gelir.
// ÇOKLU seçim alan(lar)ı: değeri virgüllü listedir ("a,b"); boş = "Tümü".
// Tester geri bildirimi: kullanıcı 2 şehri ya da 2 alt tipi aynı raporda görebilmeli.
const MULTI_KEYS = [
  'vendorIds', 'groupIds', 'subGroupIds', 'statuses',
  'regions', 'cities', 'districts', 'customerIds', 'repIds',
  'vendorGroupIds', 'vendorSubGroupIds',
];
const CRITERIA_KEYS = [
  ...MULTI_KEYS,
  'repAssignment',
  'taxNumber', 'identityNumber',
  'isEinvoice', 'isEdispatch', 'startCode', 'endCode',
  'creditOp', 'creditVal', 'createdFrom', 'createdTo',
];
// Çoklu alanlar state'te dizi tutar; query string'e CSV olarak yazılır.
const isMulti = (k) => MULTI_KEYS.includes(k);

const emptyCriteria = () =>
  CRITERIA_KEYS.reduce((acc, k) => { acc[k] = isMulti(k) ? [] : (k === 'creditOp' ? 'ge' : ''); return acc; }, {});

function criteriaToParams(c) { const p = new URLSearchParams();
  for (const k of CRITERIA_KEYS) { const v = c[k];
    if (isMulti(k)) { if (Array.isArray(v) && v.length) p.set(k, v.join(',')); continue; }
    if (v === '' || v === false || v == null) continue;
    if (k === 'creditOp') continue; // sadece creditVal doluysa anlamlı
    if (k === 'creditVal') { p.set('creditOp', c.creditOp || 'ge'); p.set('creditVal', v); continue; }
    p.set(k, String(v)); }
  return p; }

function paramsToCriteria(sp) { const c = emptyCriteria();
  for (const k of CRITERIA_KEYS) { const v = sp.get(k);
    if (v == null) continue;
    c[k] = isMulti(k) ? v.split(',').filter(Boolean) : v; }
  return c; }

// Kriterlerden OData $filter üretir (yalnız dolu alanlar).
// ÇOKLU seçim: her değer için ayrı eşitlik kurulup OR'lanır — (X eq a or X eq b).
const orEq = (field, values, quote = false) => {
  // DİKKAT: filter(Boolean) KULLANMA — Durum "Aktif"in kodu 0'dır ve sessizce düşerdi.
  const list = (values || []).filter((v) => v !== null && v !== undefined && v !== '');
  if (list.length === 0) return null;
  const one = (v) => `${field} eq ${quote ? `'${esc(v)}'` : v}`;
  return list.length === 1 ? one(list[0]) : `(${list.map(one).join(' or ')})`;
};

function buildODataFilter(c, lookups) { const cl = [];
  const push = (x) => { if (x) cl.push(x); };

  // Distribütör: doğrudan seçim + "Dist Grup / Dist Ek Grup" üzerinden dolaylı seçim.
  // Grup kriterleri Vendor tablosunda tutulduğu için önce distribütör kimliklerine çevrilir;
  // OData tarafında Customer.VendorId ile süzülür (nav property filtresine gerek kalmaz).
  const vendorsFromGroups = (() => {
    const gIds = c.vendorGroupIds || [];
    const sIds = c.vendorSubGroupIds || [];
    if (gIds.length === 0 && sIds.length === 0) return null;
    return (lookups?.vendors || [])
      .filter((v) => {
        const g = String(v.groupId ?? v.GroupId ?? '');
        const sg = String(v.subGroupId ?? v.SubGroupId ?? '');
        return (gIds.length === 0 || gIds.includes(g)) && (sIds.length === 0 || sIds.includes(sg));
      })
      .map((v) => String(optId(v)));
  })();
  // Hem doğrudan hem grup üzerinden seçim varsa KESİŞİM alınır.
  let vendorIds = c.vendorIds || [];
  if (vendorsFromGroups) {
    vendorIds = vendorIds.length ? vendorIds.filter((id) => vendorsFromGroups.includes(id)) : vendorsFromGroups;
    // Grup seçildi ama eşleşen distribütör yok → hiçbir müşteri gelmemeli.
    if (vendorIds.length === 0) return 'VendorId eq 00000000-0000-0000-0000-000000000000';
  }
  push(orEq('VendorId', vendorIds));

  push(orEq('GroupId', c.groupIds));
  push(orEq('SubGroupId', c.subGroupIds));
  push(orEq('Status', (c.statuses || []).map(Number)));
  push(orEq('Region', c.regions, true));
  push(orEq('City', c.cities, true));
  push(orEq('District', c.districts, true));
  push(orEq('Id', c.customerIds));

  if (c.taxNumber?.trim()) cl.push(`contains(TaxNumber,'${esc(c.taxNumber.trim())}')`);
  if (c.identityNumber?.trim()) cl.push(`contains(IdentityNumber,'${esc(c.identityNumber.trim())}')`);
  if (c.isEinvoice === 'true' || c.isEinvoice === 'false') cl.push(`IsEinvoice eq ${c.isEinvoice}`);
  if (c.isEdispatch === 'true' || c.isEdispatch === 'false') cl.push(`IsEdispatch eq ${c.isEdispatch}`);
  if (c.startCode?.trim()) cl.push(`TextId ge '${esc(c.startCode.trim())}'`);
  if (c.endCode?.trim()) cl.push(`TextId le '${esc(c.endCode.trim())}'`);
  if (c.creditVal !== '' && c.creditVal != null && !Number.isNaN(Number(c.creditVal))) { const op = CREDIT_OPS.some((o) => o.value === c.creditOp) ? c.creditOp : 'ge';
    cl.push(`CreditLimit1 ${op} ${Number(c.creditVal)}`); }
  if (c.createdFrom) cl.push(`CreatedDate ge ${c.createdFrom}T00:00:00Z`);
  if (c.createdTo) cl.push(`CreatedDate le ${c.createdTo}T23:59:59Z`);
  return cl.join(' and '); }

// ST (satış temsilcisi) kriterleri $filter ile YAZILAMAZ: müşteri–temsilci bağı rut planı
// üzerinden kurulur (RoutePlanCustomer → RoutePlan → RoutePlanRepresentative) ve Customer
// entity'sinde o koleksiyona navigasyon yok. Bu yüzden CustomersController.Get'e eklenen
// $-öneksiz özel parametrelerle sunucuda uygulanır.
//
// Not: müşteri kartındaki SalesRepresentativeId/RepId alanlarına bakan eski OData filtresi
// KALDIRILDI — bu alanlar veritabanında pratikte boş (6647 müşteride 0 ve 2 dolu), yani
// temsilci seçen kullanıcı sürekli boş rapor alıyordu.
function buildRepParams(c) {
  const qs = [];
  (c.repIds || []).filter(Boolean).forEach((r) => qs.push(`repIds=${encodeURIComponent(r)}`));
  if (c.repAssignment) qs.push(`repAssignment=${encodeURIComponent(c.repAssignment)}`);
  return qs.join('&');
}

// Kriterlerin insan-okur özeti (liste ekranındaki rozet için).
// Çoklu seçimler "A + B" biçiminde birleştirilir.
function criteriaSummary(c, lookups) { const parts = [];
  const joinNames = (arr, ids) => (ids || [])
    .map((id) => { const o = (arr || []).find((x) => String(optId(x)) === String(id)); return o ? optLabel(o) : id; })
    .join(' + ');
  const add = (label, text) => { if (text) parts.push(`${label}: ${text}`); };

  add('Distribütör', joinNames(lookups.vendors, c.vendorIds));
  add('Dist. Grubu', joinNames(lookups.vendorGroups, c.vendorGroupIds));
  add('Dist. Ek Grubu', joinNames(lookups.vendorSubGroups, c.vendorSubGroupIds));
  add('Temsilci', joinNames(lookups.reps, c.repIds));
  add('ST Ataması', REP_ASSIGNMENT_LABELS[c.repAssignment] || '');
  add('Kanal', joinNames(lookups.groups, c.groupIds));
  add('Alt Tip', joinNames(lookups.subGroups, c.subGroupIds));
  add('Müşteri', joinNames(lookups.customers, c.customerIds));
  add('Durum', (c.statuses || []).map((v) => STATUS_LABELS[v] ?? v).join(' + '));
  add('Bölge', (c.regions || []).join(' + '));
  add('Şehir', (c.cities || []).join(' + '));
  add('İlçe', (c.districts || []).join(' + '));

  if (c.taxNumber?.trim()) parts.push(`VKN: ${c.taxNumber.trim()}`);
  if (c.identityNumber?.trim()) parts.push(`TCKN: ${c.identityNumber.trim()}`);
  if (c.isEinvoice === 'true' || c.isEinvoice === 'false') parts.push(`E-Fatura: ${c.isEinvoice === 'true' ? 'Evet' : 'Hayır'}`);
  if (c.isEdispatch === 'true' || c.isEdispatch === 'false') parts.push(`E-İrsaliye: ${c.isEdispatch === 'true' ? 'Evet' : 'Hayır'}`);
  if (c.startCode?.trim() || c.endCode?.trim()) parts.push(`Kod aralığı: ${c.startCode || '…'} – ${c.endCode || '…'}`);
  if (c.creditVal !== '') parts.push(`Kredi Limit ${CREDIT_OPS.find((o) => o.value === c.creditOp)?.label ?? ''} ${fmtMoney(c.creditVal)}`);
  if (c.createdFrom || c.createdTo) parts.push(`Oluşturma: ${c.createdFrom || '…'} – ${c.createdTo || '…'}`);
  return parts.length ? parts.join(' · ') : 'Tüm kayıtlar — filtre yok'; }

// Grup/Alt grup/Distribütör lookup'larını tek seferde çeker.
function useLookups() {
  const [lookups, setLookups] = useState({
    vendors: [], groups: [], subGroups: [], customers: [], reps: [], vendorGroups: [], vendorSubGroups: [],
  });
  useEffect(() => { const headers = authHeaders();
    const load = (url) => fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then(normalizeList).catch(() => []);
    // Bağımsız yükle — müşteri listesi büyük olabilir, diğer kriterler onu beklemesin.
    load('/api/vendors/Select').then((vendors) => setLookups((s) => ({ ...s, vendors })));
    load('/api/customergroups/Select').then((groups) => setLookups((s) => ({ ...s, groups })));
    load('/api/customersubgroups/Select').then((subGroups) => setLookups((s) => ({ ...s, subGroups })));
    load('/api/customers/Select').then((customers) => setLookups((s) => ({ ...s, customers })));
    load('/api/representatives/Select').then((reps) => setLookups((s) => ({ ...s, reps })));
    load('/api/vendorgroups/Select').then((vendorGroups) => setLookups((s) => ({ ...s, vendorGroups })));
    load('/api/vendorsubgroups/Select').then((vendorSubGroups) => setLookups((s) => ({ ...s, vendorSubGroups })));
  }, []);
  return lookups; }

// Bir Customer alanının SİSTEMDEKİ farklı (dolu) değerlerini OData $apply ile çeker —
// böylece Bölge/Şehir gibi açılır menüler gerçek verideki değerlerle dolar.
function useDistinct(field) { const [values, setValues] = useState([]);
  useEffect(() => { const apply = `groupby((${field}),aggregate($count as Count))`;
    fetch(`${API_BASE}?$apply=${encodeURIComponent(apply)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { value: [] }))
      .then((d) => { const rows = Array.isArray(d?.value) ? d.value : [];
        const out = [];
        for (const row of rows) { const key = Object.keys(row).find((k) => k.toLowerCase() === field.toLowerCase());
          const v = key ? row[key] : null;
          if (v != null && String(v).trim() !== '') out.push(String(v).trim()); }
        out.sort((a, b) => a.localeCompare(b, 'tr'));
        setValues(out); })
      .catch(() => setValues([])); }, [field]);
  return values; }

/* ════════════════════════════ STYLES ════════════════════════════ */
const c = { page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 4 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  hTitle: { fontSize: 22, fontWeight: 800, color: 'var(--text-primary, #0f172a)', margin: 0 },
  hCrumb: { fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #64748b)', marginTop: 2 },
  card: { background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    borderRadius: 18, boxShadow: 'var(--shadow-card)', padding: 20 },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  cardHeadLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cardHeadIcon: { width: 40, height: 40, borderRadius: 12, background: 'var(--bg-input, #eef2ff)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' },
  cardTitle: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--text-secondary, #475569)' },
  input: { padding: '11px 12px', borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13.5,
    width: '100%', boxSizing: 'border-box' },
  sectionSep: { display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0 4px',
    fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', color: 'var(--text-secondary, #94a3b8)' },
  sepLine: { flex: 1, height: 1, background: 'var(--border-color, #e5e7eb)' },
  toggleRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 },
  toggle: (on) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 12,
    border: `1px solid ${on ? '#16a34a' : 'var(--border-color, #e5e7eb)'}`,
    background: on ? 'rgba(22,163,74,0.08)' : 'var(--bg-card, #fff)', cursor: 'pointer',
    fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary, #0f172a)' }),
  knob: (on) => ({ width: 38, height: 22, borderRadius: 20, background: on ? '#16a34a' : '#cbd5e1',
    position: 'relative', transition: 'background .15s', flexShrink: 0 }),
  knobDot: (on) => ({ position: 'absolute', top: 2, left: on ? 18 : 2, width: 18, height: 18, borderRadius: '50%',
    background: '#fff', transition: 'left .15s' }),
  btn: { padding: '11px 20px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost: { background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)' },
  btnPrimary: { background: 'var(--accent-strong, #1e3a8a)', color: '#fff' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '48px 24px', textAlign: 'center' },
  emptyIcon: { width: 64, height: 64, borderRadius: 18, background: 'var(--bg-input, #eef2f7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #94a3b8)', marginBottom: 6 } };

function Field({ label, children }) { return (
    <label style={c.field}>
      <span style={c.label}>{label}</span>
      {children}
    </label>
  ); }
function SelectInput({ value, onChange, placeholder = 'Tümü', options }) { return (
    <select style={c.input} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={String(optId(o) ?? o.value)} value={String(optId(o) ?? o.value)}>
          {o.label !== undefined ? o.label : optLabel(o)}
        </option>
      ))}
    </select>
  ); }

/* ═══════════════════════ 1) KRİTER EKRANI ═══════════════════════ */
export function CustomerReportCriteriaPage() { usePageMeta(REPORT_TITLE, "Sabit Raporlar › Tanımlama Listeleri");
  const lookups = useLookups();
  const regions = useDistinct('Region');   // sistemdeki gerçek bölgeler
  const cities = useDistinct('City');       // sistemdeki gerçek şehirler
  const districts = useDistinct('District'); // İlçe de artık listeden çoklu seçilir
  const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }));
  const choices = (arr) => arr.map((o) => ({ value: String(optId(o)), label: optLabel(o) }));
  // Müşteri seçicileri: kimlikle seçim (Müşteri kriteri) ve KODLA seçim (kod aralığı).
  const customerChoices = useMemo(
    () => lookups.customers.map((o) => ({ value: String(optId(o)), label: optLabel(o) })),
    [lookups.customers],
  );
  const codeChoices = useMemo(
    () => lookups.customers
      .map((o) => ({ value: String(optCode(o) ?? ''), label: optLabel(o) }))
      .filter((o) => o.value)
      .sort((a, b) => a.value.localeCompare(b.value, 'tr')),
    [lookups.customers],
  );
  const [crit, setCrit] = useState(emptyCriteria);
  const set = (k, v) => setCrit((prev) => ({ ...prev, [k]: v }));

  const showReport = () => { const params = criteriaToParams(crit);
    params.set('embed', 'true');
    // Yeni sekme yerine AYRI bir pencere olarak aç (popup + boyut → tarayıcı sekme değil pencere açar).
    const w = Math.min(1440, window.screen.availWidth - 40);
    const h = Math.min(920, window.screen.availHeight - 60);
    const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2));
    const features = `popup=yes,noopener,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`;
    window.open(`/reports/customer-list?${params.toString()}`, 'musteriListesi5020', features); };

  return (
    <div style={c.page}>

      <div style={c.card}>
        <div style={c.cardHead}>
          <div style={c.cardHeadLeft}>
            <span style={c.cardHeadIcon}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            </span>
            <span style={c.cardTitle}>Kriterler</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" style={{ ...c.btn, ...c.btnGhost }} onClick={() => setCrit(emptyCriteria())}>
              ✕ Temizle
            </button>
            <button type="button" style={{ ...c.btn, ...c.btnPrimary }} onClick={showReport}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              Raporu Göster
            </button>
          </div>
        </div>

        <div style={c.grid}>
          <Field label="Distribütör">
            <MultiSelect options={choices(lookups.vendors)} value={crit.vendorIds} onChange={(v) => set('vendorIds', v)} />
          </Field>
          <Field label="Distribütör Grubu">
            <MultiSelect options={choices(lookups.vendorGroups)} value={crit.vendorGroupIds} onChange={(v) => set('vendorGroupIds', v)} />
          </Field>
          <Field label="Distribütör Ek Grubu">
            <MultiSelect options={choices(lookups.vendorSubGroups)} value={crit.vendorSubGroupIds} onChange={(v) => set('vendorSubGroupIds', v)} />
          </Field>
          <Field label="Satış Temsilcisi">
            <MultiSelect options={choices(lookups.reps)} value={crit.repIds} onChange={(v) => set('repIds', v)} />
          </Field>
          <Field label="ST Ataması">
            <SelectInput
              value={crit.repAssignment}
              onChange={(v) => set('repAssignment', v)}
              options={REP_ASSIGNMENT_OPTIONS}
              placeholder="Tümü"
            />
          </Field>
          <Field label="Kanal (Müşteri Grubu)">
            <MultiSelect options={choices(lookups.groups)} value={crit.groupIds} onChange={(v) => set('groupIds', v)} />
          </Field>
          <Field label="Müşteri Alt Tipi">
            <MultiSelect options={choices(lookups.subGroups)} value={crit.subGroupIds} onChange={(v) => set('subGroupIds', v)} />
          </Field>
          <Field label="Müşteri Durumu">
            <MultiSelect options={STATUS_OPTIONS} value={crit.statuses} onChange={(v) => set('statuses', v)} />
          </Field>
          <Field label="Müşteri">
            <MultiSelect options={customerChoices} value={crit.customerIds} onChange={(v) => set('customerIds', v)} placeholder="Tümü — listeden seçin" />
          </Field>

          <Field label="Bölge">
            <MultiSelect options={asOptions(regions)} value={crit.regions} onChange={(v) => set('regions', v)} />
          </Field>
          <Field label="Şehir">
            <MultiSelect options={asOptions(cities)} value={crit.cities} onChange={(v) => set('cities', v)} />
          </Field>
          <Field label="İlçe">
            <MultiSelect options={asOptions(districts)} value={crit.districts} onChange={(v) => set('districts', v)} />
          </Field>
          <Field label="Vergi Numarası">
            <input
              style={c.input}
              placeholder="En fazla 10 rakam"
              value={crit.taxNumber}
              inputMode="numeric"
              maxLength={10}
              onChange={(e) => set('taxNumber', digitsOnly(e.target.value, 10))}
            />
          </Field>
          <Field label="TC Kimlik No">
            <input
              style={c.input}
              placeholder="En fazla 11 rakam"
              value={crit.identityNumber}
              inputMode="numeric"
              maxLength={11}
              onChange={(e) => set('identityNumber', digitsOnly(e.target.value, 11))}
            />
          </Field>

          <Field label="E-Fatura Mükellefi">
            <SelectInput value={crit.isEinvoice} onChange={(v) => set('isEinvoice', v)} options={YESNO_OPTIONS} />
          </Field>
          <Field label="E-İrsaliye Mükellefi">
            <SelectInput value={crit.isEdispatch} onChange={(v) => set('isEdispatch', v)} options={YESNO_OPTIONS} />
          </Field>
          <Field label="Kredi Limit (1)">
            <div style={{ display: 'flex', gap: 8 }}>
              <select style={{ ...c.input, width: 78, flexShrink: 0 }} value={crit.creditOp} onChange={(e) => set('creditOp', e.target.value)}>
                {CREDIT_OPS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input style={c.input} type="number" placeholder="Tutar…" value={crit.creditVal} onChange={(e) => set('creditVal', e.target.value)} />
            </div>
          </Field>
          {/* Kod aralığı da elle yazılmıyor: müşteri listesinden seçilir, seçilen müşterinin KODU alınır. */}
          <Field label="Başlangıç Müşteri Kodu">
            <SelectInput
              value={crit.startCode}
              onChange={(v) => set('startCode', v)}
              options={codeChoices}
              placeholder="Listeden seçin"
            />
          </Field>
          <Field label="Bitiş Müşteri Kodu">
            <SelectInput
              value={crit.endCode}
              onChange={(v) => set('endCode', v)}
              options={codeChoices}
              placeholder="Listeden seçin"
            />
          </Field>
        </div>

        <div style={c.sectionSep}><span>OLUŞTURULMA TARİHİ</span><span style={c.sepLine} /></div>
        <div style={c.grid}>
          <Field label="Başlangıç">
            <DateInput value={crit.createdFrom} onChange={(v) => set('createdFrom', v)} />
          </Field>
          <Field label="Bitiş">
            <DateInput value={crit.createdTo} onChange={(v) => set('createdTo', v)} />
          </Field>
        </div>
      </div>

      <div style={c.card}>
        <div style={c.emptyState}>
          <span style={c.emptyIcon}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
          </span>
          <strong style={{ fontSize: 17, color: 'var(--text-primary, #0f172a)' }}>Müşteri listesi ayrı ekranda görüntülenir</strong>
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary, #64748b)', maxWidth: 520 }}>
            İstediğiniz filtreleri seçip <strong>Raporu Göster</strong>’e basın — liste ayrı bir pencerede tam ekran açılır,
            kolon başlıklarından süzebilir, Excel/PDF olarak indirebilir veya yazdırabilirsiniz.
          </span>
        </div>
      </div>
    </div>
  ); }

/* ═══════════════════════ 2) LİSTE EKRANI ═══════════════════════ */
const l = { wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-page, #f1f5f9)' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '14px 20px', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #e5e7eb)',
    position: 'sticky', top: 0, zIndex: 5 },
  topLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 10,
    border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-input, #f8fafc)',
    color: 'var(--text-primary, #0f172a)', fontWeight: 800, fontSize: 13, cursor: 'pointer' },
  titleIcon: { width: 42, height: 42, borderRadius: 12, background: 'var(--accent-strong, #1e3a8a)', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  subtitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary, #64748b)' },
  iconBtn: { width: 42, height: 42, borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)',
    background: 'var(--bg-card, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  critBar: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px',
    background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #eef2f7)', fontSize: 12.5 },
  critChip: { padding: '6px 12px', borderRadius: 999, background: 'var(--bg-input, #f1f5f9)',
    color: 'var(--text-secondary, #475569)', fontWeight: 700, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tableScroll: { flex: 1, overflow: 'auto', padding: '0 8px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: 'var(--bg-card, #fff)' },
  th: { position: 'sticky', top: 0, background: 'var(--bg-card, #f8fafc)', textAlign: 'left',
    padding: '12px 14px', fontWeight: 800, color: 'var(--text-secondary, #64748b)', fontSize: 11.5, letterSpacing: '0.03em',
    borderBottom: '2px solid var(--border-color, #e5e7eb)', whiteSpace: 'nowrap', zIndex: 1, textTransform: 'uppercase', cursor: 'pointer' },
  td: { padding: '12px 14px', borderBottom: '1px solid var(--border-color, #eef2f7)', whiteSpace: 'nowrap', color: 'var(--text-primary, #0f172a)' },
  codeChip: { padding: '4px 10px', borderRadius: 8, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent)',
    fontWeight: 800, fontSize: 12.5 },
  chanChip: { padding: '3px 10px', borderRadius: 999, background: 'rgba(139,92,246,0.12)', color: '#7c3aed', fontWeight: 800, fontSize: 12 },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
    padding: '12px 20px', background: 'var(--bg-card, #fff)', borderTop: '1px solid var(--border-color, #e5e7eb)',
    position: 'sticky', bottom: 0 },
  pageBtn: (active, disabled) => ({ minWidth: 36, height: 36, borderRadius: 9, border: '1px solid var(--border-color, #e5e7eb)',
    background: active ? 'var(--accent-strong, #1e3a8a)' : 'var(--bg-card, #fff)',
    color: active ? '#fff' : 'var(--text-primary, #0f172a)', fontWeight: 800, fontSize: 13,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, padding: '0 8px' }),
  empty: { padding: 60, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
  thInner: { display: 'flex', alignItems: 'center', gap: 6 },
  funnelBtn: (active) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, flexShrink: 0,
    borderRadius: 6, cursor: 'pointer', padding: 0,
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border-color, #e5e7eb)'}`,
    background: active ? 'var(--accent)' : 'var(--bg-card, #fff)',
    color: active ? '#fff' : 'var(--text-secondary, #94a3b8)' }),
  backdrop: { position: 'fixed', inset: 0, zIndex: 40 },
  popover: { position: 'fixed', zIndex: 41, width: 250, padding: 12, borderRadius: 12,
    background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)',
    boxShadow: '0 12px 32px rgba(15,23,42,0.18)', display: 'flex', flexDirection: 'column', gap: 10 },
  popTitle: { fontSize: 12, fontWeight: 800, color: 'var(--text-secondary, #64748b)', textTransform: 'none' },
  popBtns: { display: 'flex', justifyContent: 'flex-end', gap: 8 } };

function buildLabelMap(list) { const m = new Map();
  list.forEach((o) => m.set(String(optId(o)), optLabel(o)));
  return m; }

// Kolon sözleşmesi ReportGrid'e uygundur: filtre/sıralama/sayfalama grid içinde (TanStack).
// `sort` alanı yalnız print başlığı için tutulur; `cell/fmt/sticky` grid içindir. Print
// fonksiyonu bu diziyi `label/num/key` üzerinden okumaya devam eder — o alanlar korunmuştur.
const LIST_COLUMNS = [
  { key: 'textId', label: 'Müşteri Kodu', sort: 'TextId', sticky: true, cell: (row) => <span style={l.codeChip}>{row.textId ?? '—'}</span> },
  { key: 'title', label: 'Müşteri Adı', sort: 'Title', cell: (row) => <strong>{row.title ?? '—'}</strong> },
  { key: 'channel', label: 'Kanal', cell: (row) => (row.channel ? <span style={l.chanChip}>{row.channel}</span> : '—') },
  { key: 'subType', label: 'Alt Tip' },
  { key: 'city', label: 'Şehir', sort: 'City' },
  { key: 'district', label: 'İlçe', sort: 'District' },
  { key: 'phone', label: 'Telefon' },
  { key: 'taxOffice', label: 'Vergi Dairesi', sort: 'TaxOffice' },
  { key: 'taxNumber', label: 'Vergi Numarası', sort: 'TaxNumber' },
  { key: 'dueDate', label: 'Vade Günü', sort: 'DueDate', num: true, fmt: (v) => nf2.format(Number(v) || 0) },
  { key: 'creditLimit1', label: 'Kredi Limiti-1', sort: 'CreditLimit1', num: true, fmt: fmtMoney },
  { key: 'creditLimit2', label: 'Kredi Limiti-2', sort: 'CreditLimit2', num: true, fmt: fmtMoney },
];

const hesc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// PDF olarak indir: tüm satırları temiz bir HTML tabloya döküp gizli iframe + print ile
// yazdırır (kullanıcı "PDF olarak kaydet" seçer). Repo'nun deste-bağımsız PDF yaklaşımı.
function printReportAsPdf({ rows, groupMap, subGroupMap, summary, totalCount }) { const headCells = LIST_COLUMNS.map((col) => `<th style="text-align:${col.num ? 'right' : 'left'}">${hesc(col.label)}</th>`).join('');
  const bodyRows = rows.map((row) => { const tds = LIST_COLUMNS.map((col) => { let v;
      switch (col.key) { case 'channel': v = groupMap.get(String(row.groupId)) ?? '—'; break;
        case 'subType': v = subGroupMap.get(String(row.subGroupId)) ?? '—'; break;
        case 'phone': v = row.phone || row.cellPhone || '—'; break;
        case 'dueDate': v = nf2.format(Number(row.dueDate) || 0); break;
        case 'creditLimit1': v = fmtMoney(row.creditLimit1); break;
        case 'creditLimit2': v = fmtMoney(row.creditLimit2); break;
        default: v = row[col.key] ?? '—'; }
      return `<td style="text-align:${col.num ? 'right' : 'left'}">${hesc(v)}</td>`; }).join('');
    return `<tr>${tds}</tr>`; }).join('');

  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${REPORT_TITLE}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; margin: 0; padding: 18px; }
  .rhead { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #1e3a8a; padding-bottom: 8px; margin-bottom: 6px; }
  .rhead h1 { font-size: 16px; margin: 0; color: #1e3a8a; }
  .rhead .meta { font-size: 11px; color: #64748b; text-align: right; }
  .crit { font-size: 10.5px; color: #475569; margin: 6px 0 10px; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #eef2f7; color: #475569; font-size: 9.5px; text-transform: uppercase; letter-spacing: .03em; padding: 6px 7px; border-bottom: 1.5px solid #cbd5e1; }
  td { font-size: 10.5px; padding: 5px 7px; border-bottom: 1px solid #eef2f7; white-space: nowrap; }
  tr:nth-child(even) td { background: #fafcff; }
  @page { size: A4 landscape; margin: 12mm; }
</style></head><body>
  <div class="rhead">
    <h1>${REPORT_TITLE}</h1>
    <div class="meta">${totalCount.toLocaleString('tr-TR')} müşteri</div>
  </div>
  <div class="crit"><strong>Kriterler:</strong> ${hesc(summary)}</div>
  <table><thead><tr>${headCells}</tr></thead><tbody>${bodyRows}</tbody></table>
</body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  const cleanup = () => setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1000);
  iframe.onload = () => { iframe.contentWindow.focus();
    iframe.contentWindow.print();
    cleanup(); }; }

export function CustomerReportListPage() { usePageMeta(REPORT_TITLE, 'Sabit Rapor');
  const [sp] = useSearchParams();
  const lookups = useLookups();

  const criteria = useMemo(() => paramsToCriteria(sp), [sp]);
  const filterParams = useMemo(() => buildODataFilter(criteria, lookups), [criteria, lookups]);
  const repParams = useMemo(() => buildRepParams(criteria), [criteria]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Filtre/sıralama/sayfalama artık ReportGrid'in içinde (kalıcı görünüm). Excel/PDF kullanıcının
  // GÖRDÜĞÜ kümeyi almalı → grid görünen satırları buraya bildirir. null = grid henüz bildirmedi.
  const [visibleRows, setVisibleRows] = useState(null);

  const groupMap = useMemo(() => buildLabelMap(lookups.groups), [lookups.groups]);
  const subGroupMap = useMemo(() => buildLabelMap(lookups.subGroups), [lookups.subGroups]);

  const fetchData = useCallback(async () => { setLoading(true);
    setError(null);
    try { // Kriter filtresine uyan TÜM satırları tek seferde çek — filtre/sıralama/sayfalama
      // ReportGrid'de client-side yapılır (max 100k).
      const base = buildODataUrl(API_BASE, { pageSize: 100000, currentPage: 1, sortField: 'TextId', sortDir: 'asc', filterParams });
      // ST kriterleri $-öneksiz özel parametre olarak eklenir (bkz. buildRepParams).
      const url = repParams ? `${base}${base.includes('?') ? '&' : '?'}${repParams}` : base;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rapor alınamadı (${res.status})`);
      const data = await res.json();
      setRows(getODataRows(data)); } catch (e) { setError(e.message || 'Hata');
      setRows([]); } finally { setLoading(false); } }, [filterParams, repParams]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Grid'e verilen satırlar: Kanal/Alt Tip/Telefon lookup'tan çözülmüş metinlerle zenginleştirilir
  // (grid'in filtre/sıralama/arama'sı gerçek metin değerleri üzerinde çalışsın).
  const gridRows = useMemo(
    () => rows.map((r) => ({ ...r,
      channel: groupMap.get(String(r.groupId)),
      subType: subGroupMap.get(String(r.subGroupId)),
      phone: r.phone || r.cellPhone || undefined })),
    [rows, groupMap, subGroupMap],
  );

  // Grid görünen satırları bildirir (filtre + sıralama uygulanmış); Excel/PDF bunu kullanır.
  const exportRows = visibleRows ?? gridRows;

  const summary = useMemo(() => criteriaSummary(criteria, lookups), [criteria, lookups]);

  const onExcel = useCallback(() => { const cols = [
      { field: 'textId', headerName: 'Müşteri Kodu' },
      { field: 'title', headerName: 'Müşteri Adı' },
      { headerName: 'Kanal', valueGetter: ({ row }) => row.channel ?? '' },
      { headerName: 'Alt Tip', valueGetter: ({ row }) => row.subType ?? '' },
      { field: 'city', headerName: 'Şehir' },
      { field: 'district', headerName: 'İlçe' },
      { headerName: 'Telefon', valueGetter: ({ row }) => row.phone || row.cellPhone || '' },
      { field: 'taxOffice', headerName: 'Vergi Dairesi' },
      { field: 'taxNumber', headerName: 'Vergi Numarası' },
      { field: 'dueDate', headerName: 'Vade Günü' },
      { field: 'creditLimit1', headerName: 'Kredi Limiti-1' },
      { field: 'creditLimit2', headerName: 'Kredi Limiti-2' },
    ];
    exportToExcel(exportRows, cols, `${REPORT_CODE}_musteri_listesi`); }, [exportRows]);

  const onPdf = useCallback(() => { printReportAsPdf({ rows: exportRows, groupMap, subGroupMap, summary, totalCount: exportRows.length }); }, [exportRows, groupMap, subGroupMap, summary]);

  return (
    <div style={l.wrap}>
      <div style={l.topbar}>
        <div style={l.topLeft}>
          <span style={l.titleIcon}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
          </span>
          <div>
            <div style={l.title}>{REPORT_TITLE}</div>
            <div style={l.subtitle}>{loading ? 'Yükleniyor…' : `${rows.length.toLocaleString('tr-TR')} müşteri`}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" style={l.iconBtn} title="PDF İndir" onClick={onPdf}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15h1.5a1.5 1.5 0 0 0 0-3H9v6" /><path d="M15.5 12H14v6" /><path d="M14 15h1.5" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Excel'e Aktar" onClick={onExcel}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#16794c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="m9 15 3 3 3-3" /><path d="M12 12v6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Yazdır" onClick={() => window.print()}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          </button>
        </div>
      </div>

      <div style={l.critBar}>
        <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary, #94a3b8)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
        <span style={{ fontWeight: 800, color: 'var(--text-secondary, #64748b)' }}>KRİTERLER</span>
        <span style={l.critChip} title={summary}>{summary}</span>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 20px 20px' }}>
        {error && <div style={{ ...l.empty, color: '#dc2626' }}>{error}</div>}
        {!error && !loading && (
          <ReportGrid
            reportKey="customer-report"
            columns={LIST_COLUMNS}
            rows={gridRows}
            onVisibleRowsChange={setVisibleRows}
          />
        )}
        {loading && <div style={l.empty}>Yükleniyor…</div>}
      </div>
    </div>
  ); }
